import { ReactNode } from 'react';
import {
  IconExclamationCircle,
  IconX,
  TablerIcon,
} from '@rossum/ui/icons/tabler';
import {
  IconButton,
  Snackbar as MUISnackbar,
  SnackbarContent,
  snackbarContentClasses,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { blend } from '@rossum/ui/system';
import { borderRadius } from '@rossum/ui/theme';

type SnackbarProps = {
  open: boolean;
  content: string;
  dataTestId: string;
  Icon?: TablerIcon;
  isError?: boolean;
  onClose?: () => void;
  action?: ReactNode;
};

export const Snackbar = ({
  content,
  open,
  dataTestId,
  isError = false,
  onClose,
  action,
}: SnackbarProps) => {
  return (
    <MUISnackbar
      open={open}
      sx={{ p: 0, position: 'static' }}
      data-testid={dataTestId}
    >
      <SnackbarContent
        sx={{
          p: 0,
          border: '1px solid',
          borderColor: t =>
            isError ? t.palette.error.main : t.palette.other.outlinedBorder,
          borderRadius: borderRadius('m'),
          backgroundColor: t =>
            blend(t.palette.background.default, t.palette.error.main, 0.1),
          ...(isError ? { backgroundImage: 'none' } : {}),
          [`& .${snackbarContentClasses.message}`]: {
            width: '100%',
            minWidth: 350,
            maxWidth: 850,
            px: 1.5,
            py: 1,
          },
        }}
        elevation={6}
        message={
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              {isError && (
                <Stack
                  sx={{
                    color: t => t.palette.error.main,
                  }}
                >
                  <SvgIcon color="inherit" fontSize="medium">
                    <IconExclamationCircle />
                  </SvgIcon>
                </Stack>
              )}
              <Typography
                component="div"
                color="textPrimary"
                variant="body2"
                sx={{ width: 'max-content', maxWidth: 400 }}
              >
                {content}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap={0.5}
              sx={{ flex: 1 }}
            >
              {!!action && action}
              {!!onClose && (
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{ my: -1 }}
                  data-testid={`${dataTestId}-close-button`}
                >
                  <SvgIcon fontSize="medium" color="action">
                    <IconX />
                  </SvgIcon>
                </IconButton>
              )}
            </Stack>
          </Stack>
        }
      />
    </MUISnackbar>
  );
};
